
import { Interval, DateTime, Duration } from "luxon";

import {Filter, DomainFilter, UnitSystem} from '../utils/Filter'

type AttributionSource = 'Sources' | 'ESWD' | 'NIWA';
const ATTRIBUTION_LINKS: Record<AttributionSource, string> = {
    'Sources': 'https://tornadoarchive.com/home/tornado-archive-data-explorer/sources/',
    'ESWD': 'https://eswd.eu',
    'NIWA': 'https://hwe.niwa.co.nz',
}

const DATA_LIMITS = Interval.fromDateTimes(DateTime.utc(1, 1, 1, 0), DateTime.utc(2024, 1, 1, 0));
const USE_DEV_SERVER = process.env.NODE_ENV == 'development' || process.env.REACT_APP_BUILD == 'development' 
                       || process.env.REACT_APP_BUILD == 'beta';

interface TimelineSegmentSpec {
    domain: Interval;
    scale: number;
}

const TIMELINE_SEGMENTS: TimelineSegmentSpec[] = [
    {'domain': Interval.fromDateTimes(DateTime.utc(-200000, 1, 1, 0), DateTime.utc(1500, 1, 1, 0)), 'scale': 0.01},
    {'domain': Interval.fromDateTimes(DateTime.utc(1500, 1, 1, 0), DateTime.utc(1900, 1, 1, 0)), 'scale': 0.1},
    {'domain': Interval.fromDateTimes(DateTime.utc(1900, 1, 1, 0), DateTime.utc(3000, 1, 1, 0)), 'scale': 1}
]

interface DomainSpec {
    fit_bounds: [[number, number], [number, number]];
    filter: Filter;
    cday_offset: Duration;
    units: UnitSystem;
    has_states: boolean;
    attribution: AttributionSource[];
    env_name: string;
    small_datafile_intvs: [number | null, number | null][];
}

type Domain = 'World' | 'North America' | 'South America' | 'Europe' | 'Western Asia' | 'Eastern Asia' | 'Southern Africa' | 'Oceania';

const DOMAINS: Record<Domain, DomainSpec> = {
    'World': {
        'fit_bounds': [[-180, -75], [180, 75]],
        'filter': new DomainFilter(),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'NIWA', 'Sources'],
        'env_name': 'world',
        'small_datafile_intvs': [],
    },
    'North America': {
        'fit_bounds': [[-169, 8], [-51, 72]],
        'filter': new DomainFilter(['us', 'ca', 'mx', 'cu', 'ni', 'noa']),
        'cday_offset': Duration.fromObject({'hours': 12}),
        'units': 'imperial',
        'has_states': true,
        'attribution': ['Sources'],
        'env_name': 'na',
        'small_datafile_intvs': ([[null, 1899], [1900, 1924], [1925, 1949]] as [number | null, number][]).concat(
            Array.from({'length': 74}, (yr, iyr) : [number, number] => [1950 + iyr, 1950 + iyr])
        ),
    },
    'South America': {
        'fit_bounds': [[-83, -57], [-32, 15]],
        'filter': new DomainFilter(['ar', 'uy', 'cl', 'br', 'py', 've', 'co', 'soa']),
        'cday_offset': Duration.fromObject({'hours': 12}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['Sources'],
        'env_name': 'sa',
        'small_datafile_intvs': [[null, null]],
    },
    'Europe': {
        'fit_bounds': [[-25, 28], [41, 70]],
        'filter': new DomainFilter(['eu', 'al', 'at', 'by', 'be', 'ba', 'bg', 'hr', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr', 'ge',
                                    'de', 'gr', 'hu', 'is', 'ie', 'it', 'xk', 'lv', 'lt', 'lu', 'mk', 'mt', 'md', 'me', 'nl',
                                    'no', 'pl', 'pt', 'ro', 'ru', 'rs', 'sk', 'si', 'es', 'se', 'ch', 'ua', 'gb', 'tn', 'ma',
                                    'dz', 'ly']),
        'cday_offset': Duration.fromObject({'hours': 6}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'Sources'],
        'env_name': 'eu',
        'small_datafile_intvs': ([[null, 1899], [1900, 1939], [1940, 1979], [1980, 1989], [1990, 1999]] as [number | null, number][]).concat(
            Array.from({'length': 22}, (yr, iyr) => [2000 + iyr, 2000 + iyr])
        ),
    },
    'Western Asia': {
        'fit_bounds': [[34, 30], [90, 60]],
        'filter': new DomainFilter(['ru', 'tr', 'kg', 'tm', 'kz', 'ge', 'il', 'sy', 'az', 'am', 'lb', 'jo', 'sa', 'qa', 'om', 
                                    'ae', 'bh', 'iq', 'wea']),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'Sources'],
        'env_name': 'was',
        'small_datafile_intvs': [[null, 1999], [2000, 2009], [2010, 2015], [2016, 2023]],
    },
    'Eastern Asia': {
        'fit_bounds': [[67, -10], [190, 60]],
        'filter': new DomainFilter(['ru', 'jp', 'cn', 'id', 'in', 'bd', 'mn', 'eaa']),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'Sources'],
        'env_name': 'eas',
        'small_datafile_intvs': [[null, 1999], [2000, 2009], [2010, 2014], [2015, 2023]],
    },
    'Southern Africa': {
        'fit_bounds': [[11, -35], [52, -8]],
        'filter': new DomainFilter(['za', 'mg', 'sof']),
        'cday_offset': Duration.fromObject({'hours': 6}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['Sources'],
        'env_name': 'saf',
        'small_datafile_intvs': [[null, null]],  
    },
    'Oceania': {
        'fit_bounds': [[112, -48], [181, -10]],
        'filter': new DomainFilter(['au', 'nz', 'oca']),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['NIWA', 'Sources'],
        'env_name': 'oc',
        'small_datafile_intvs': [[null, null]],
    }
};
const DOMAIN_TITLES = Object.keys(DOMAINS) as Domain[];

const ENV_INTV_DUR_CUTOFF = Duration.fromISO('PT72H');
const ENV_DATA_BASE = USE_DEV_SERVER ? '/dev/env' : '/env';

const TIMELINE_MARGINS = {'top': 5, 'bottom': 30, 'left': 40, 'right': 55};

type TemporalDisplayOpt = 'All' | 'Hourly' | 'None';
type GIFQualityOpt = 'low' | 'medium' | 'high';
type FileDownloadFormat = 'kml' | 'csv' | 'geojson';

interface MapState {
    center_lon: number;
    center_lat: number;
    zoom: number;
}

export {DATA_LIMITS, USE_DEV_SERVER, DOMAINS, DOMAIN_TITLES, ENV_INTV_DUR_CUTOFF, ENV_DATA_BASE, TIMELINE_MARGINS, TIMELINE_SEGMENTS, ATTRIBUTION_LINKS};
export type {AttributionSource, Domain, DomainSpec, TemporalDisplayOpt, GIFQualityOpt, MapState, TimelineSegmentSpec, FileDownloadFormat}